import { Injectable } from '@angular/core';
import { DeviceInfoRequest } from 'edu-auth/dist/request/device.info.request';
import { UAParser } from "ua-parser-js";
import { v4 as uuid } from 'uuid';
@Injectable({
  providedIn: 'root'
})
export class DevicesService {

  constructor() { }

  async getDeviceInfo(): Promise<DeviceInfoRequest> {
    const parser = new UAParser();
    const ua = parser.getResult();
    const deviceInfo: DeviceInfoRequest = {
      fingerprint: await this.createCanvasFingerprint() ?? '',
      browserName: ua.browser.name ?? '',
      browserVersion: ua.browser.version ?? '',
      engineName: ua.engine.name ?? '',
      engineVersion: ua.engine.version ?? '',
      cpu: ua.cpu.architecture ?? '',
      os: ua.os.name ?? '',
      osVersion: ua.os.version ?? '',
      type: ua.device.type ?? '',
      vendor: ua.device.vendor ?? '',
      model: ua.device.model ?? '',
      eduId: this.getEduId(),
      ipAddress: '', // This should be set to the user's IP address, which may require
    };
    return deviceInfo;
  }
  /**
   * Enhanced canvas fingerprinting with multiple rendering techniques
   */
  private async createCanvasFingerprint(): Promise<string | null> {
    try {
      const canvas = document.createElement('canvas');

      // Set specific dimensions for consistency
      canvas.width = 280;
      canvas.height = 120;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.warn('Canvas 2D context not available');
        return null;
      }

      // Clear canvas and set background
      ctx.fillStyle = '#f8f9fa';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Test 1: Complex text rendering with rotation
      ctx.save();
      const complexText = 'i9asdm..$#po((^@KbXrww!~cz_2025';
      ctx.textBaseline = "top";
      ctx.font = "16px 'Arial', sans-serif";
      ctx.textBaseline = "alphabetic";
      ctx.rotate(0.05);
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText(complexText, 2, 15);
      ctx.fillStyle = "rgba(102, 200, 0, 0.7)";
      ctx.fillText(complexText, 4, 17);
      ctx.restore();

      // Test 2: Shadow effects and gradients
      ctx.shadowBlur = 10;
      ctx.shadowColor = "blue";
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = "rgba(255, 99, 71, 0.8)";
      ctx.fillRect(-20, 10, 234, 5);

      // Test 3: Gradient rendering
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, '#ff6b6b');
      gradient.addColorStop(0.5, '#4ecdc4');
      gradient.addColorStop(1, '#45b7d1');
      ctx.fillStyle = gradient;
      ctx.fillRect(20, 30, 200, 15);

      // Test 4: Complex shapes with composite operations
      ctx.shadowBlur = 0; // Reset shadow
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = 'rgba(255, 0, 150, 0.6)';
      ctx.beginPath();
      ctx.arc(80, 70, 25, 0, Math.PI * 2);
      ctx.fill();

      // Test 5: Different fonts and unicode
      ctx.globalCompositeOperation = 'source-over';
      ctx.font = "bold 14px 'Times New Roman', serif";
      ctx.fillStyle = '#2c3e50';
      ctx.fillText('🎓📚 Education™ ©2025', 10, 90);

      // Test 6: Bezier curves
      ctx.strokeStyle = '#e74c3c';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(200, 50);
      ctx.bezierCurveTo(220, 30, 240, 80, 260, 60);
      ctx.stroke();

      // Test 7: Pattern rendering (if supported)
      try {
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = 10;
        patternCanvas.height = 10;
        const patternCtx = patternCanvas.getContext('2d');
        if (patternCtx) {
          patternCtx.fillStyle = '#3498db';
          patternCtx.fillRect(0, 0, 5, 5);
          patternCtx.fillStyle = '#e67e22';
          patternCtx.fillRect(5, 5, 5, 5);

          const pattern = ctx.createPattern(patternCanvas, 'repeat');
          if (pattern) {
            ctx.fillStyle = pattern;
            ctx.fillRect(200, 80, 50, 30);
          }
        }
      } catch (error) {
        // Pattern creation failed, continue without it
      }

      // Extract canvas data
      const dataURL = canvas.toDataURL('image/png');

      // Hash the canvas data
      const hashBase64 = await this.hashString(dataURL);

      return hashBase64;

    } catch (error) {
      console.warn('Canvas fingerprinting failed:', error);
      return null;
    }
  }

  /**
     * Hash a string using Web Crypto API
     */
  private async hashString(input: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);

      // Convert to base64
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashBase64 = btoa(String.fromCharCode(...hashArray));

      return hashBase64;
    } catch (error) {
      // Fallback to simple hash if Web Crypto API fails
      console.warn('Web Crypto API failed, using fallback hash');
      return this.fallbackHash(input);
    }
  }

  /**
   * Simple fallback hash function
   */
  private fallbackHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }


  private getEduId(): string {
    const DEVICE_ID_KEY = '_deId';
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  }
}
